import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { enqueueSnackbar } from 'notistack';
import camelCase from 'lodash/camelCase';
import stringify from 'fast-json-stable-stringify';
import { MUIButton } from '@fuww/library/src/Button';
import JobPageComponent from '@fuww/library/src/Jobs';
import {
  JobsDesktopOnlyContainer,
  JobsMobileOnlyContainer,
  DesktopOnlyContainer,
} from '@fuww/library/src/ImageContainer';
import Anchor from '@fuww/library/src/Anchor';
import Skeleton from '@fuww/library/src/SkeletonLoader/Skeleton';
import CompactButton from '@fuww/library/src/Button/CompactButton';
import Head from '../../Head';
import HomeLink from '../../HomeLink';
import { useSiteContext } from '../../SiteContext';
import ImageWithUrls from '../../ImageWithUrls';
import LinkAndAnchor from '../../LinkAndAnchor';
import HeaderImage, {
  MobileOnlyImage,
  DesktopOnlyImage,
} from '../../Company/HeaderImage';
import filterQuery from '../../../lib/filterQuery';
import messages from '../../../lib/messages.json';
import throw404 from '../../../lib/throw404';
import getDescriptionText from '../../../lib/getDescriptionText';
import getJobsRoute from '../../../lib/getJobsRoute';
import debouncedReplaceRoute from '../../../lib/debouncedReplaceRoute';
import {
  getLocalizedCategoryParameters,
  categoryParametersToCategory,
  originalCategoryParameters,
} from '../../../lib/jobCategories';
import { saveRouterState } from '../../../lib/savedRouterState';
import replaceRoute from '../../../lib/replaceRoute';
import safelyDecodeURIComponent from '../../../lib/safelyDecodeUriComponent';
import {
  jobSearchListLogoImageConfigurations,
  wfbAdsJobsPageImageConfigurations,
} from '../../../lib/imageConfigurations';
import JobSearchFilters from './JobSearchFilters';
import JobList from './JobSearchList';
import JobSearchTabs from './JobSearchTabs';
import JobSearchNews from './JobSearchNews';
import InView from '../../InView';
import DynamicWfbAds from '../../Company/DynamicWfbAds';

const PER_PAGE = 25;
const ALL_CATEGORIES_KEY = 'all-categories';
const resetSearchParameters = {
  keywords: null,
  locationSearch: null,
  categoryParam: null,
};

const JobSearchCompanyLogo = ({
  isCareerPage,
  profile,
  image,
}) => {
  const {
    page: {
      careerSiteReturnUrl,
    },
  } = profile;

  return isCareerPage && careerSiteReturnUrl ? (
    <Anchor
      href={careerSiteReturnUrl}
    >
      {image}
    </Anchor>
  ) : image;
};

JobSearchCompanyLogo.propTypes = {
  image: PropTypes.node.isRequired,
  isCareerPage: PropTypes.bool.isRequired,
  profile: PropTypes.shape({
    canUpdate: PropTypes.bool,
    company: PropTypes.shape({
      displayName: PropTypes.string,
      logoUrls: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }),
    page: PropTypes.shape({
      careerSiteReturnUrl: PropTypes.string,
    }),
  }).isRequired,
};

const JobSearch = ({
  title,
  description,
  pageTitle,
  introduction,
  variables,
  categoryLabel,
  profile,
  city,
  position,
  headerImage,
  logo,
  hasTabs,
  showJobsPageLoader,
  skipHighlightedJobsQuery,
  skipMainJobsListQuery,
  handleAllLocalesFilterChange,
  showJobsForAllLocales,
  setCoordinates,
  sortByDistance,
  wfbAds,
  postJobButton,
}) => {
  const intl = useIntl();
  const { locale, jobsComplete, isCareerPage } = useSiteContext();
  const { query } = filterQuery(useRouter());
  const {
    categoryParam,
    keywords: keywordsParameter,
    locationSearch: locationSearchParameter,
    companySlug,
  } = query;
  const { profilePage, ...rest } = query;
  const parametersWithResetSearch = {
    ...rest,
    ...resetSearchParameters,
  };
  const {
    page: {
      isEbpEnabled,
      showAboutPage,
      showNewsPage,
      showWorkingAtPage,
    } = {},
  } = { ...profile };

  if (!showJobsPageLoader && profilePage && !isEbpEnabled) {
    throw404();
  }

  const handleSortByDistanceClick = useCallback(() => {
    const updatedQuery = {
      ...query,
      citySlug: null,
      locationSearch: null,
      sort: sortByDistance ? null : 'distance',
    };
    replaceRoute(
      getJobsRoute(updatedQuery, locale, isCareerPage),
      updatedQuery,
    );
  }, [
    query,
    locale,
    sortByDistance,
    isCareerPage,
  ]);

  const sortByDistanceOption = useMemo(
    () => ({
      id: 'sortByDistance',
      name: intl.formatMessage(messages['jobs.jobsNearMe']),
      onClick: () => {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            setCoordinates({
              latitude: coords.latitude,
              longitude: coords.longitude,
            });
            handleSortByDistanceClick();
          },
          ({ code }) => {
            const permissionDenied = code === 1;
            enqueueSnackbar(
              permissionDenied
                ? intl.formatMessage(messages.enableBrowserLocation)
                : intl.formatMessage(messages.errorBrowserLocation),
              { variant: 'info' },
            );
          },
        );
      },
      selected: sortByDistance,
    }),
    [
      setCoordinates,
      sortByDistance,
      handleSortByDistanceClick,
      intl,
    ],
  );

  const categoryBreadcrumbs = categoryParam ? [
    <LinkAndAnchor
      route={`jobs.${locale}`}
      params={{ ...query, keywords: null, locationSearch: null }}
      useRouter={jobsComplete}
    >
      {categoryLabel}
    </LinkAndAnchor>,
  ] : [];

  const profileBreadcrumbs = profile ? [
    <LinkAndAnchor
      route={`jobs.${locale}`}
      params={parametersWithResetSearch}
      useRouter={jobsComplete}
    >
      {profile.company.displayName}
    </LinkAndAnchor>,
  ] : [];

  const cityBreadcrumbs = city ? [
    <LinkAndAnchor
      route={`jobs.${locale}`}
      params={parametersWithResetSearch}
      useRouter={jobsComplete}
    >
      {city.name}
    </LinkAndAnchor>,
  ] : [];

  const positionBreadcrumbs = position ? [
    <LinkAndAnchor
      route={`jobs.${locale}`}
      params={parametersWithResetSearch}
      useRouter={jobsComplete}
    >
      {position.name}
    </LinkAndAnchor>,
  ] : [];

  const breadcrumbs = isCareerPage ? [] : [
    <HomeLink />,
    <LinkAndAnchor
      route={`jobs.${locale}`}
      params={{
        ...parametersWithResetSearch,
        companySlug: null,
        citySlug: null,
        positionSlug: null,
      }}
      useRouter={jobsComplete}
    >
      {intl.formatMessage(messages.jobs)}
    </LinkAndAnchor>,
    ...profileBreadcrumbs,
    ...cityBreadcrumbs,
    ...positionBreadcrumbs,
    ...categoryBreadcrumbs,
  ];

  const showJobsList = !profilePage;
  const showNewsList = companySlug && profilePage === 'news';
  const showTabs = isEbpEnabled && [
    showAboutPage, showNewsPage, showWorkingAtPage,
  ].some(Boolean);

  const [keywords, setKeywords] = useState(
    safelyDecodeURIComponent(keywordsParameter || ''),
  );

  const [locationSearch, setLocationSearch] = useState(safelyDecodeURIComponent(
    locationSearchParameter || '',
  ));

  useEffect(() => {
    saveRouterState(
      isCareerPage ? 'careers' : 'jobs',
      { query },
    );
  }, [query, isCareerPage]);

  useEffect(() => {
    const urlLocationSearch = safelyDecodeURIComponent(
      locationSearchParameter || '',
    );
    setLocationSearch(urlLocationSearch);
  }, [locationSearchParameter]);

  const stringifiedQuery = stringify(query);
  const localizedCategories = stringify(originalCategoryParameters.map(
    (parameter) => ({
      label: intl.formatMessage(
        messages[`jobs.category.${parameter}.label`],
      ),
      value: intl.formatMessage(
        messages[`jobs.category.${parameter}.param`],
      ),
    }),
  ));
  const categoryOptions = useMemo(
    () => JSON.parse(localizedCategories).map(({ label, value }) => ({
      id: value,
      label,
      onClick: () => {
        const updatedQuery = {
          ...JSON.parse(stringifiedQuery),
          categoryParam: categoryParam === value ? null : value,
        };
        replaceRoute(
          getJobsRoute(updatedQuery, locale, isCareerPage),
          updatedQuery,
        );
      },
      selected: categoryParam === value,
    })),
    [
      categoryParam,
      isCareerPage,
      locale,
      stringifiedQuery,
      localizedCategories,
    ],
  );
  const jobsFilterVariables = {
    locales: variables.locales,
    profileId: variables.profileId,
    profileIdsOrKeywords: variables.profileIdsOrKeywords,
    positionId: variables.positionId,
    category: variables.category,
    keywords: variables.keywords,
  };

  return (
    <>
      <Head
        title={title}
        description={description}
        payload={query}
      />
      <div />
      <JobPageComponent
        hideIntroTextOnMobile={Boolean(profilePage)}
        breadcrumbs={breadcrumbs}
        title={pageTitle || title}
        introduction={introduction}
        description={getDescriptionText(profile, query)}
        hasTabs={hasTabs}
        tabs={showTabs && (
          <JobSearchTabs
            profile={profile}
            params={query}
          />
        )}
        profileEditButton={profile?.canUpdate && (
          <LinkAndAnchor
            route={`company-ebp-edit.${locale}`}
            params={query}
          >
            <MUIButton>
              {intl.formatMessage(messages.editProfile)}
            </MUIButton>
          </LinkAndAnchor>
        )}
        search={showJobsList && (
          <JobSearchFilters
            handleAllLocalesFilterChange={handleAllLocalesFilterChange}
            showJobsForAllLocales={showJobsForAllLocales}
            allLocalesCheckboxLabel={
              intl.formatMessage(messages['jobs.showJobsForAllCountries'])
            }
            locale={locale}
            keywords={keywords}
            searchFieldTitle={intl.formatMessage(messages.searchTitle)}
            onSearchChange={({ target: { value } }) => {
              setKeywords(value);
              const updatedQuery = { ...query, keywords: value || null };
              debouncedReplaceRoute(
                getJobsRoute(updatedQuery, locale, isCareerPage),
                updatedQuery,
              );
            }}
            onSearchTrailingIconSelect={() => {
              setKeywords('');
              const updatedQuery = { ...query, keywords: null };
              replaceRoute(
                getJobsRoute(updatedQuery, locale, isCareerPage),
                updatedQuery,
              );
            }}
            locationSearch={locationSearch}
            locationSearchFieldTitle={intl.formatMessage(messages.location)}
            onLocationSearchChange={({ target: { value } }) => {
              setLocationSearch(value);
              const updatedQuery = {
                ...query,
                locationSearch: value || null,
                citySlug: null,
                sort: null,
              };
              debouncedReplaceRoute(
                getJobsRoute(updatedQuery, locale, isCareerPage),
                updatedQuery,
              );
            }}
            onLocationSearchTrailingIconSelect={() => {
              setLocationSearch('');
              const updatedQuery = {
                ...query,
                locationSearch: null,
                sort: null,
              };
              replaceRoute(
                getJobsRoute(updatedQuery, locale, isCareerPage),
                updatedQuery,
              );
            }}
            categoryOptions={categoryOptions}
            jobsFilterVariables={showJobsPageLoader ? {} : jobsFilterVariables}
            showJobsPageLoader={showJobsPageLoader}
            sortByDistanceInsertion={[sortByDistanceOption]}
            onJobsNearMeClick={sortByDistanceOption.onClick}
            jobsNearMeSelected={sortByDistance}
          />
        )}
        headerImage={headerImage}
        logo={logo}
        wfbAds={wfbAds}
        postJobButton={postJobButton}
      >
        {showJobsList && (
          <JobList
            variables={variables}
            skipHighlightedJobsQuery={
              skipHighlightedJobsQuery ?? showJobsPageLoader
            }
            skipMainJobsListQuery={skipMainJobsListQuery ?? showJobsPageLoader}
          />
        )}
        {showNewsList && (
          <JobSearchNews
            tagSlugs={profile?.company?.newsTags}
          />
        )}
      </JobPageComponent>
    </>
  );
};

JobSearch.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  pageTitle: PropTypes.node,
  introduction: PropTypes.node,
  variables: PropTypes.shape({
    locales: PropTypes.arrayOf(PropTypes.string),
    category: PropTypes.string,
    keywords: PropTypes.string,
    profileId: PropTypes.string,
    profileIdsOrKeywords: PropTypes.shape({
      profileIds: PropTypes.arrayOf(PropTypes.string),
      keywords: PropTypes.string,
    }),
    positionId: PropTypes.string,
    locationSearch: PropTypes.string,
  }),
  categoryLabel: PropTypes.string,
  profile: PropTypes.shape({
    canUpdate: PropTypes.bool,
    company: PropTypes.shape({
      displayName: PropTypes.string,
      newsTags: PropTypes.arrayOf(PropTypes.string),
    }),
    page: PropTypes.shape({
      isEbpEnabled: PropTypes.bool,
      showAboutPage: PropTypes.bool,
      showWorkingAtPage: PropTypes.bool,
      showNewsPage: PropTypes.bool,
    }),
  }),
  city: PropTypes.shape({ name: PropTypes.string }),
  position: PropTypes.shape({ name: PropTypes.string }),
  headerImage: PropTypes.node,
  logo: PropTypes.node,
  hasTabs: PropTypes.bool,
  showJobsPageLoader: PropTypes.bool,
  handleAllLocalesFilterChange: PropTypes.func,
  showJobsForAllLocales: PropTypes.bool,
  skipHighlightedJobsQuery: PropTypes.bool,
  skipMainJobsListQuery: PropTypes.bool,
  setCoordinates: PropTypes.func.isRequired,
  sortByDistance: PropTypes.bool,
  wfbAds: PropTypes.node,
  postJobButton: PropTypes.node,
};

JobSearch.defaultProps = {
  title: null,
  description: null,
  pageTitle: null,
  introduction: null,
  variables: {},
  categoryLabel: null,
  profile: null,
  city: null,
  position: null,
  headerImage: null,
  logo: null,
  hasTabs: false,
  showJobsPageLoader: false,
  handleAllLocalesFilterChange: undefined,
  showJobsForAllLocales: undefined,
  skipHighlightedJobsQuery: undefined,
  skipMainJobsListQuery: undefined,
  sortByDistance: false,
  wfbAds: undefined,
  postJobButton: undefined,
};

const JobSearchPage = ({
  data, field, notFound, showLoader,
}) => {
  const intl = useIntl();
  const { jobsLocale: locale } = useSiteContext();
  const { query, route } = useRouter();
  const isCareerPage = route === '/careers';
  const {
    categoryParam,
    keywords: keywordsParameter,
    locationSearch: locationSearchParameter,
    locales,
    sort,
  } = query;
  const keywords = keywordsParameter && safelyDecodeURIComponent(
    keywordsParameter,
  );
  const locationSearch = locationSearchParameter && safelyDecodeURIComponent(
    locationSearchParameter,
  );
  const localizedCategoryParameters = getLocalizedCategoryParameters(intl);
  const originalCategoryParameter = localizedCategoryParameters[
    categoryParam
  ];
  const category = categoryParametersToCategory[originalCategoryParameter];
  const showWfbAds = (
    !categoryParam && !keywordsParameter && !locationSearchParameter && !sort
  );

  const [coordinates, setCoordinates] = useState();
  const sortByDistance = Boolean(sort === 'distance' && coordinates);

  useEffect(() => {
    if (sort === 'distance' && !coordinates) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          setCoordinates({
            latitude: coords.latitude,
            longitude: coords.longitude,
          });
        },
        () => {
          const updatedQuery = { ...query, sort: null };
          debouncedReplaceRoute(
            getJobsRoute(updatedQuery, locale, isCareerPage),
            updatedQuery,
          );
        },
      );
    }
  }, [sort, coordinates, query, locale, isCareerPage]);

  const sortByDistanceVariables = sortByDistance ? {
    sort: 'DISTANCE_ASCENDING',
    distanceFrom: {
      type: 'Point',
      coordinates: [coordinates.latitude, coordinates.longitude],
    },
  } : {};

  const showJobsForAllLocales = locales === 'all';

  const handleAllLocalesFilterChange = useCallback(
    ({ target: { checked } }) => {
      const { locales: _locales, ...rest } = query;
      const updatedQuery = { ...query, keywords: null };

      replaceRoute(
        getJobsRoute(updatedQuery, locale, isCareerPage), {
          ...rest,
          ...(checked ? { locales: 'all' } : {}),
        },
      );
    }, [query, locale, isCareerPage],
  );

  if (!showLoader && categoryParam && !category) {
    return throw404();
  }

  const variables = {
    locales: showJobsForAllLocales ? null : [locale],
    category,
    keywords,
    locationSearch,
    first: PER_PAGE,
    logoConfigurations: jobSearchListLogoImageConfigurations,
    ...sortByDistanceVariables,
  };

  const categoryKey = originalCategoryParameter || ALL_CATEGORIES_KEY;

  const categoryLabel = intl.formatMessage(
    messages[`jobs.category.${categoryKey}.label`],
  );

  if (notFound) {
    return (
      <JobSearch
        title={`${intl.formatMessage(messages.oops)
        } ${intl.formatMessage(messages['jobs.error.notFound.title'])
        }`}
        description={intl.formatMessage(
          messages[`jobs.category.${categoryKey}.description`],
        )}
        pageTitle={(
          <>
            {intl.formatMessage(messages.oops)}
            <br />
            {intl.formatMessage(messages['jobs.error.notFound.title'])}
          </>
        )}
        introduction={(
          <>
            {intl.formatMessage(messages['jobs.error.notFound.introduction'])}
            .
          </>
        )}
        categoryLabel={categoryLabel}
        variables={variables}
        showJobsPageLoader={showLoader}
        field={field}
        setCoordinates={setCoordinates}
        sortByDistance={sortByDistance}
      />
    );
  }

  switch (field) {
    case 'profileBySlug': {
      const profile = data?.[field];

      if (!showLoader && !profile) return throw404();

      const {
        id, hasTabs, company, page,
      } = { ...profile };

      const {
        displayName,
        description,
        descriptions,
        logoUrls,
      } = { ...company };
      const { [camelCase(locale)]: localizedDescription } = { ...descriptions };

      return (
        <JobSearch
          title={intl.formatMessage(
            messages['jobs.company.title'],
            { company: displayName },
          )}
          description={intl.formatMessage(
            messages['jobs.company.description'],
            { company: displayName },
          )}
          introduction={(
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: localizedDescription || description,
              }}
            />
          )}
          categoryLabel={categoryLabel}
          variables={{
            ...variables,
            profileIdsOrKeywords: {
              profileIds: [id],
              keywords: page?.jobKeywords,
            },
          }}
          profile={profile}
          headerImage={showLoader ? (
            <Skeleton variant="rectangular" width="100%">
              <DesktopOnlyContainer />
            </Skeleton>
          ) : (
            <HeaderImage
              MobileOnlyContainer={JobsMobileOnlyContainer}
              DesktopOnlyContainer={JobsDesktopOnlyContainer}
              profile={profile}
              disableRoundedCorners
            />
          )}
          logo={showLoader ? (
            <Skeleton variant="rectangular" width="220px" height="220px" />) : (
            logoUrls[0] && (
            <JobSearchCompanyLogo
              isCareerPage={isCareerPage}
              profile={profile}
              image={(
                <ImageWithUrls
                  alt={displayName}
                  sizes="220px"
                  imageUrls={logoUrls}
                  objectFit="scale-down"
                  height="220px"
                  srcSet={`${logoUrls[1]
                  } 220w, ${logoUrls[2]
                  } 440w, ${logoUrls[3]
                  } 660w`}
                  width="220px"
                />
              )}
            />
            ))}
          hasTabs={hasTabs}
          showJobsPageLoader={showLoader}
          skipHighlightedJobsQuery={showLoader || !id}
          skipMainJobsListQuery={showLoader || !id}
          handleAllLocalesFilterChange={handleAllLocalesFilterChange}
          showJobsForAllLocales={showJobsForAllLocales}
          field={field}
          setCoordinates={setCoordinates}
          sortByDistance={sortByDistance}
        />
      );
    }
    case 'cityBySlug': {
      const city = data?.[field];

      if (!showLoader && !city) return throw404();

      const { id, name, imageUrls } = { ...city };

      return (
        <JobSearch
          title={intl.formatMessage(
            messages['jobs.city.title'],
            { city: name },
          )}
          description={intl.formatMessage(
            messages['jobs.city.description'],
            { city: name },
          )}
          introduction={intl.formatMessage(
            messages['jobs.city.introduction'],
            { city: name },
          )}
          categoryLabel={categoryLabel}
          variables={{ ...variables, cityId: id }}
          city={city}
          headerImage={showLoader ? (
            <Skeleton variant="rectangular" width="100%">
              <DesktopOnlyContainer />
            </Skeleton>
          ) : (imageUrls[0] && (
            <>
              <MobileOnlyImage
                title={name}
                imageUrls={[
                  imageUrls[0],
                  imageUrls[1],
                  imageUrls[2],
                ]}
                MobileOnlyContainer={JobsMobileOnlyContainer}
              />
              <DesktopOnlyImage
                title={name}
                imageUrls={[
                  imageUrls[0],
                  imageUrls[3],
                  imageUrls[4],
                  imageUrls[5],
                ]}
                DesktopOnlyContainer={JobsDesktopOnlyContainer}
              />
            </>
          ))}
          showJobsPageLoader={showLoader}
          skipMainJobsListQuery={showLoader || !id}
          field={field}
          setCoordinates={setCoordinates}
          sortByDistance={sortByDistance}
        />
      );
    }
    case 'positionBySlug': {
      const position = data[field];

      if (!showLoader && !position) return throw404();

      const positionKey = camelCase(locale);
      const { id, name, names } = { ...position };

      return (
        <JobSearch
          title={intl.formatMessage(
            messages['jobs.position.title'],
            { position: names?.[positionKey] ?? name },
          )}
          description={intl.formatMessage(
            messages['jobs.position.description'],
            { position: name },
          )}
          introduction={intl.formatMessage(
            messages['jobs.position.introduction'],
            { position: name },
          )}
          categoryLabel={categoryLabel}
          variables={{ ...variables, positionId: id }}
          position={position}
          showJobsPageLoader={showLoader}
          skipMainJobsListQuery={showLoader || !id}
          field={field}
          setCoordinates={setCoordinates}
          sortByDistance={sortByDistance}
        />
      );
    }
    default: {
      return (
        <JobSearch
          title={intl.formatMessage(
            messages[`jobs.category.${categoryKey}.title`],
          )}
          description={intl.formatMessage(
            messages[`jobs.category.${categoryKey}.description`],
          )}
          introduction={(
            <>
              { intl.formatMessage(
                messages[`jobs.category.${categoryKey}.introduction`],
              )}
              {categoryKey === ALL_CATEGORIES_KEY && (
                <>
                  {' '}
                  <LinkAndAnchor route={`company-profiles.${locale}`}>
                    { intl.formatMessage(
                      messages[`jobs.category.${categoryKey}.discover`],
                    )}
                  </LinkAndAnchor>
                  {intl.formatMessage(messages.fullStop)}
                </>
              )}
            </>
          )}
          categoryLabel={categoryLabel}
          variables={variables}
          showJobsPageLoader={showLoader}
          field={field}
          setCoordinates={setCoordinates}
          sortByDistance={sortByDistance}
          wfbAds={showWfbAds && (
            <InView>
              <DynamicWfbAds
                limit={4}
                gridGap="40px"
                preload
                imageConfigurations={wfbAdsJobsPageImageConfigurations}
                sizes="202.5px"
              />
            </InView>
          )}
          postJobButton={!isCareerPage && (
            <LinkAndAnchor
              useRouter
              route={`submit-job-postings.${locale}`}
            >
              <CompactButton
                variant="text"
                role="link"
              >
                {intl.formatMessage(messages.postJob)}
              </CompactButton>
            </LinkAndAnchor>
          )}
        />
      );
    }
  }
};

JobSearchPage.propTypes = {
  data: PropTypes.shape({}),
  field: PropTypes.string,
  notFound: PropTypes.bool,
  showLoader: PropTypes.bool.isRequired,
};

JobSearchPage.defaultProps = {
  data: {},
  field: null,
  notFound: false,
};

export default JobSearchPage;
